import { BLOCKS } from '@contentful/rich-text-types';

import { ApiMethods } from 'src/services/api/appApi';

import { appAPI } from '.';

export const WHATS_NEW_CONTENT_TYPE = 'whatsNewAnnouncement';

// The writing guidelines cap an announcement at three feature rows; enforce it
// here so an over-long entry can't break the modal layout.
const MAX_BULLETS = 3;

export interface WhatsNewButton {
  label: string;
  url: string;
}

export interface WhatsNewAnnouncement {
  id: string;
  publishDate: string;
  header: string;
  bullets: string[];
  imageUrl: string;
  imageAlt: string;
  adminsOnly: boolean;
  primaryButton?: WhatsNewButton;
  secondaryButton?: WhatsNewButton;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readString = (record: Record<string, unknown>, key: string) => {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
};

const readRecord = (record: Record<string, unknown>, key: string) => {
  const value = record[key];
  return isRecord(value) ? value : undefined;
};

const readButton = (
  fields: Record<string, unknown>,
  labelKey: string,
  urlKey: string,
): WhatsNewButton | undefined => {
  const label = readString(fields, labelKey)?.trim();
  const url = readString(fields, urlKey)?.trim();
  return label && url ? { label, url } : undefined;
};

const readNodes = (node: unknown): unknown[] =>
  isRecord(node) && Array.isArray(node.content) ? node.content : [];

const readText = (node: unknown): string => {
  if (!isRecord(node)) return '';
  const value = readString(node, 'value') ?? '';
  return value + readNodes(node).map(readText).join('');
};

const LIST_BLOCKS: unknown[] = [BLOCKS.UL_LIST, BLOCKS.OL_LIST];

// `bullets` is a Rich Text field: each list item is a bullet, and a plain
// paragraph counts as one too so authors don't have to use list formatting.
const readBullets = (document: unknown): string[] =>
  readNodes(document)
    .flatMap((block) =>
      isRecord(block) && LIST_BLOCKS.includes(block.nodeType)
        ? readNodes(block).map(readText)
        : [readText(block)],
    )
    .map((bullet) => bullet.trim())
    .filter(Boolean)
    .slice(0, MAX_BULLETS);

const findAsset = (assets: unknown[], assetId: string | undefined) => {
  if (!assetId) return undefined;
  return assets
    .filter(isRecord)
    .find(
      (asset) => readString(readRecord(asset, 'sys') ?? {}, 'id') === assetId,
    );
};

const parseAnnouncement = (
  item: unknown,
  assets: unknown[],
): WhatsNewAnnouncement | undefined => {
  if (!isRecord(item)) return undefined;
  const sys = readRecord(item, 'sys');
  const fields = readRecord(item, 'fields');
  if (!sys || !fields) return undefined;

  const id = readString(sys, 'id');
  // The proxy only passes `sys.id` through, so ordering comes from an authored date field.
  const publishDate = readString(fields, 'publishDate');
  const header = readString(fields, 'header')?.trim();
  if (!id || !publishDate || !header) return undefined;

  const bullets = readBullets(fields.bullets);

  const imageLink = readRecord(fields, 'image');
  const asset = findAsset(
    assets,
    readString(readRecord(imageLink ?? {}, 'sys') ?? {}, 'id'),
  );
  const assetFields = asset ? readRecord(asset, 'fields') : undefined;
  const imageUrl = readString(
    readRecord(assetFields ?? {}, 'file') ?? {},
    'url',
  );
  // An announcement without its image would render a broken half-modal.
  if (!imageUrl) return undefined;

  return {
    id,
    publishDate,
    header,
    bullets,
    imageUrl,
    imageAlt: readString(assetFields ?? {}, 'title') ?? header,
    adminsOnly: fields.adminsOnly === true,
    primaryButton: readButton(fields, 'primaryButtonLabel', 'primaryButtonUrl'),
    secondaryButton: readButton(
      fields,
      'secondaryButtonLabel',
      'secondaryButtonUrl',
    ),
  };
};

/** Parses the Contentful proxy response into well-formed announcements, dropping malformed entries. */
export const parseWhatsNewAnnouncements = (
  response: unknown,
): WhatsNewAnnouncement[] => {
  if (!isRecord(response) || !Array.isArray(response.items)) return [];
  const includes = readRecord(response, 'includes');
  const assets =
    includes && Array.isArray(includes.Asset) ? includes.Asset : [];

  return response.items.flatMap((item) => {
    const announcement = parseAnnouncement(item, assets);
    return announcement ? [announcement] : [];
  });
};

const whatsNewApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getWhatsNewAnnouncements: build.query<WhatsNewAnnouncement[], void>({
      query: () => ({
        path: '/data/contentful',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            select: 'sys.id,fields',
            contentType: WHATS_NEW_CONTENT_TYPE,
          },
        },
      }),
      transformResponse: (response: unknown) =>
        parseWhatsNewAnnouncements(response),
    }),
  }),
});

export const { useGetWhatsNewAnnouncementsQuery } = whatsNewApi;
